import React, { useEffect, useState } from "react";
import {
  NewspaperIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { fetchPosts } from "../../features/admin/PostsSlice";
import { fetchCategoryByType } from "../../features/admin/CategorySlice";
import CustomTableComponent from "../../components/CustomTableComponent";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

function AdminPostStatisticCard() {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("userId");

  const { authPosts: posts, authPostStatus: status } = useSelector(
    (state) => state.posts,
  );
  const { categories } = useSelector((state) => state.categories);

  const [approved, setApproved] = useState(0);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch posts, categories
  useEffect(() => {
    if (status === "idle") dispatch(fetchPosts());
    dispatch(fetchCategoryByType("notification,navbar,post,"));
  }, [dispatch, status]);

  // Lọc dữ liệu đúng logic trang Posts
  useEffect(() => {
    if (!posts || !categories) return;
    const filtered = posts
      .filter(
        (post) =>
          userId == 1 ||
          categories.some((category) => category.id === post.categoryId),
      )
      .filter((post) => post.type !== "notification");
    setTotal(filtered.length);
    setApproved(filtered.filter((p) => p.status === "published").length);
  }, [posts, categories, userId]);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full shadow-md sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-blue-100 bg-gradient-to-br from-white to-blue-50/30 hover:border-blue-200 transition-all cursor-pointer"
        onClick={() => setModalOpen(true)}
      >
        <div className="p-3 bg-blue-100 rounded-full mb-3">
          <NewspaperIcon className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-lg text-black">Bài viết đã duyệt</p>
        <p className="text-3xl font-bold mt-1">
          <span>{approved}</span>
          <span className="text-3xl font-bold"> / </span>
          <span>{total}</span>
        </p>
      </motion.div>
      <PostApproveModal
        open={modalOpen}
        onClose={() => {
          dispatch(fetchPosts());
          setModalOpen(false);
        }}
        posts={posts ? posts.filter((p) => p.type !== "notification") : []}
        categories={categories || []}
        userId={userId}
        onReload={() => dispatch(fetchPosts())}
      />
    </div>
  );
}

// Modal danh sách & duyệt bài viết (Duyệt/bỏ duyệt = select)
function PostApproveModal({
  open,
  onClose,
  posts,
  categories,
  userId,
  onReload,
}) {
  const [filter, setFilter] = useState("Tất cả");
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [approving, setApproving] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [nextStatus, setNextStatus] = useState("published");

  // Filter bài viết đúng với categories/user
  useEffect(() => {
    setFilteredPosts(
      posts
        ? posts
            .filter(
              (post) =>
                userId == 1 ||
                categories.some((cat) => cat.id === post.categoryId),
            )
            .filter((p) =>
              filter === "Tất cả"
                ? true
                : filter === "Đã duyệt"
                  ? p.status === "published"
                  : p.status !== "published",
            )
        : [],
    );
  }, [posts, categories, userId, filter]);

  // Xử lý duyệt/bỏ duyệt qua select
  const handleApprove = async (id, currentStatus) => {
    setApproving(id);
    const nextStatus = currentStatus === "published" ? "draft" : "published";
    try {
      const res = await ApiCustom.put(`/posts-approve/${id}`, {
        status: nextStatus,
      });
      if (res.status === 200) {
        toast.success(
          nextStatus === "published"
            ? "Đã duyệt bài viết!"
            : "Đã chuyển bài viết về trạng thái chờ duyệt!",
        );
        setConfirmId(null);
        onReload && onReload();
      } else {
        toast.error("Có lỗi khi cập nhật trạng thái bài viết.");
      }
    } catch {
      toast.error("Có lỗi khi cập nhật trạng thái bài viết.");
    } finally {
      setApproving(null);
    }
  };

  // Chi tiết bài viết: fetch API by id
  const handleRowClick = async (item) => {
    setDetailLoading(true);
    setDetailId(item.id);
    try {
      const res = await ApiCustom.get(`/posts/${item.id}`);
      setSelected(res?.data?.data || item);
    } catch {
      setSelected(item);
    } finally {
      setDetailLoading(false);
    }
  };

  const headers = [
    {
      key: "title",
      label: "Tiêu đề",
      render: (item) => (
        <span className="font-medium">{item.title || "--"}</span>
      ),
    },
    {
      key: "category",
      label: "Danh mục",
      render: (item) =>
        item.category
          ? item.category.name
          : categories.find((c) => c.id === item.categoryId)?.name || "--",
    },
    {
      key: "createdAt",
      label: "Ngày tạo",
      render: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("vi-VN")
          : "--",
    },
    {
      key: "actions",
      label: "Thao tác",
      render: (item) => (
        <div className="text-center">
          {item.status !== "published" ? (
            <button
              className="btn btn-outline btn-success btn-xs flex items-center gap-1"
              disabled={!!approving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmId(item.id);
                setNextStatus("published");
              }}
            >
              <CheckCircleIcon className="w-4 h-4 hidden md:block" />
              Duyệt
            </button>
          ) : (
            <button
              className="btn btn-outline btn-error btn-xs flex items-center gap-1"
              disabled={!!approving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmId(item.id);
                setNextStatus("draft");
              }}
            >
              <XMarkIcon className="w-4 h-4 hidden md:block" />
              Bỏ duyệt
            </button>
          )}
          {confirmId === item.id && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xs relative">
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                  onClick={() => setConfirmId(null)}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
                <div className="text-lg mb-3 font-semibold">
                  {item.status !== "published"
                    ? "Xác nhận duyệt bài viết?"
                    : "Xác nhận bỏ duyệt bài viết?"}
                </div>
                <div className="text-gray-500 mb-4 text-sm">
                  Bạn chắc chắn muốn{" "}
                  {item.status !== "published" ? "duyệt" : "bỏ duyệt"} bài viết{" "}
                  <br />
                </div>
                <div className="flex justify-end space-x-2 mt-2">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setConfirmId(null)}
                  >
                    Hủy
                  </button>
                  <button
                    className={`btn btn-success btn-sm px-4 ${approving ? "opacity-60 pointer-events-none" : ""}`}
                    onClick={() => handleApprove(item.id, item.status)}
                  >
                    {approving === item.id
                      ? item.status !== "published"
                        ? "Đang duyệt..."
                        : "Đang bỏ duyệt..."
                      : "Xác nhận"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...animation}
          className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
        >
          <motion.div
            {...animation}
            className="relative bg-white w-full max-w-7xl mx-4 rounded-xl p-6 border border-gray-200 overflow-y-auto shadow-xl"
            style={{ maxHeight: "90vh" }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex justify-between items-center mb-4 mr-4">
              <h2 className="text-xl font-bold">Danh sách bài viết</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border rounded px-3 py-2 focus:outline-none focus:border-blue-400"
              >
                <option value="Tất cả">Tất cả</option>
                <option value="Chờ duyệt">Chờ duyệt</option>
                <option value="Đã duyệt">Đã duyệt</option>
              </select>
            </div>
            <CustomTableComponent
              headers={headers}
              data={filteredPosts}
              onRowClick={confirmId ? () => {} : handleRowClick}
            />
            <PostDetailModal
              open={!!detailId}
              loading={detailLoading}
              onClose={() => {
                setDetailId(null);
                setSelected(null);
              }}
              post={selected}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Modal chi tiết bài viết (fetch detail)
function PostDetailModal({ open, loading, onClose, post }) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white rounded-2xl max-w-6xl w-full p-8 shadow-xl border border-blue-100"
          style={{ maxHeight: "90vh", overflow: "auto" }}
          initial={{ scale: 0.96 }}
          animate={{ scale: 1, transition: { duration: 0.2 } }}
          exit={{ scale: 0.96, transition: { duration: 0.15 } }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          {loading ? (
            <div className="py-10 text-center text-gray-500">
              Đang tải chi tiết...
            </div>
          ) : !post ? (
            <div className="py-10 text-center text-gray-500">
              Không tìm thấy bài viết
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-2">
                <NewspaperIcon className="h-6 w-6 text-blue-500" />
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                  {post.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 mb-6">
                <div>
                  <div>
                    Trạng thái:{" "}
                    <span
                      className={`font-medium ${
                        post.status === "published"
                          ? "text-green-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {post.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
                    </span>
                  </div>
                  <div>
                    Ngày tạo:{" "}
                    {post.createdAt
                      ? new Date(post.createdAt).toLocaleDateString("vi-VN")
                      : "--"}
                  </div>
                </div>
                <div className="md:flex md:justify-center">
                  <div>
                    <div>
                      Danh mục:{" "}
                      <span className="text-blue-700 font-medium">
                        {post.category?.name || "--"}
                      </span>
                    </div>
                    <div>
                      Người tạo:{" "}
                      <span className="font-medium">
                        {post.user?.userFullName || "--"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-2 border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Nội dung chi tiết
                </h3>
                <div
                  className="text-sm leading-relaxed max-h-[65vh] text-gray-800 bg-gray-50 p-4 rounded-lg border"
                  style={{
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: post.content || "<i>Không có nội dung.</i>",
                  }}
                />
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AdminPostStatisticCard;
